import { useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { Group, Vector3 } from 'three'
import { Container, Text } from '@react-three/uikit'
import { useInstances } from '../scene/instances'
import { faceUserQuat, type Vec3 } from '../scene/spawn'
import { COKE_CAN_SIZE } from '../scene/constants'
import { stepScale } from '../features/grab/logic'

const _v = new Vector3()
const MENU_MARGIN = 0.08 // clearance between the model surface and the menu

export function ModelMenu() {
  const instance = useInstances((s) => s.list.find((i) => i.instanceId === s.selectedId))
  const setScale = useInstances((s) => s.setScale)
  const setPhysics = useInstances((s) => s.setPhysics)
  const select = useInstances((s) => s.select)
  const camera = useThree((s) => s.camera)
  const groupRef = useRef<Group>(null)

  useFrame(() => {
    if (groupRef.current == null || instance == null) return
    // Place the menu between the user and the model, offset by the model's half-extent so a large
    // model never occludes it. Model is centered on instance.position; rendered max extent =
    // COKE_CAN_SIZE * scale, so half-extent (a safe radius for any axis) is half of that.
    const head = camera.getWorldPosition(_v)
    const [mx, my, mz] = instance.position
    let dx = head.x - mx
    let dz = head.z - mz
    const len = Math.hypot(dx, dz) || 1
    dx /= len
    dz /= len
    const radius = (COKE_CAN_SIZE * instance.scale) / 2 + MENU_MARGIN
    const pos: Vec3 = [mx + dx * radius, my, mz + dz * radius]
    groupRef.current.position.set(pos[0], pos[1], pos[2])
    groupRef.current.quaternion.fromArray(faceUserQuat(pos, head.toArray()))
  })

  if (instance == null) return null

  const rescale = (factor: number) =>
    setScale(instance.instanceId, stepScale(instance.scale, factor))

  return (
    <group ref={groupRef}>
      {/* The menu floats just in front of the grabbable model; without stopping propagation a ray
          aimed straight through a button also hits the model's grab Handle behind it, which
          captures the pointerdown and swallows the click (clicking only worked at an off-angle). */}
      <Container
        flexDirection="row"
        alignItems="center"
        gap={8}
        padding={10}
        backgroundColor="#171717"
        borderRadius={12}
        pixelSize={0.0012}
        onPointerDown={(e) => e.stopPropagation()}
        onPointerUp={(e) => e.stopPropagation()}
      >
        <Container
          paddingX={14}
          paddingY={8}
          borderRadius={10}
          backgroundColor="#262626"
          hover={{ backgroundColor: '#3f3f46' }}
          onClick={() => rescale(0.8)}
        >
          <Text fontSize={20} color="#fafafa">
            −
          </Text>
        </Container>

        <Container width={70} justifyContent="center">
          <Text fontSize={15} color="#fafafa">
            {`×${instance.scale.toFixed(2)}`}
          </Text>
        </Container>

        <Container
          paddingX={14}
          paddingY={8}
          borderRadius={10}
          backgroundColor="#262626"
          hover={{ backgroundColor: '#3f3f46' }}
          onClick={() => rescale(1.25)}
        >
          <Text fontSize={20} color="#fafafa">
            +
          </Text>
        </Container>

        <Container
          paddingX={12}
          paddingY={8}
          borderRadius={10}
          backgroundColor={instance.physics ? '#15803d' : '#262626'}
          hover={{ backgroundColor: instance.physics ? '#16a34a' : '#3f3f46' }}
          onClick={() => setPhysics(instance.instanceId, !instance.physics)}
        >
          <Text fontSize={15} color="#fafafa">
            {instance.physics ? 'Physics on' : 'Physics off'}
          </Text>
        </Container>

        <Container
          paddingX={12}
          paddingY={8}
          borderRadius={10}
          backgroundColor="#5b21b6"
          hover={{ backgroundColor: '#6d28d9' }}
          onClick={() => select(null)}
        >
          <Text fontSize={15} color="#fafafa">
            Close
          </Text>
        </Container>
      </Container>
    </group>
  )
}
